import { useEffect, useState } from 'react';
import { useExtensionsStore } from '@/stores/extensionsStore';
import { getImagePalette, MAX_COLORS, type ColorFormat, type Swatch } from './palette';

export const COLOR_PALETTE_ID = 'color-palette';

export interface PaletteSettings {
  enabled: boolean;
  colors: number;
  format: ColorFormat;
}

export function usePaletteSettings(): PaletteSettings {
  const { loaded, load } = useExtensionsStore();
  // Subscribe to rows so turning it on or saving settings shows up without a reload.
  useExtensionsStore((s) => s.rows);

  useEffect(() => {
    if (!loaded) load();
  }, [loaded, load]);

  const { isEnabled, getSettings } = useExtensionsStore.getState();
  const values = getSettings(COLOR_PALETTE_ID);
  return {
    enabled: isEnabled(COLOR_PALETTE_ID),
    colors: Math.min(MAX_COLORS, Number(values.colors ?? 5)),
    format: values.format === 'rgb' ? 'rgb' : 'hex',
  };
}

/** The palette for an image URL: undefined while reading, null when it can't be read. */
export function useImagePalette(url: string | undefined, active: boolean): Swatch[] | null | undefined {
  const [palette, setPalette] = useState<{ url: string; swatches: Swatch[] | null } | null>(null);

  useEffect(() => {
    if (!active || !url) return;
    let cancelled = false;
    getImagePalette(url).then((swatches) => {
      if (!cancelled) setPalette({ url, swatches });
    });
    return () => {
      cancelled = true;
    };
  }, [url, active]);

  if (!url || palette?.url !== url) return undefined;
  return palette.swatches;
}
